import React, { useEffect, useRef, useState } from "react";
import { ExternalLink, RefreshCw, BarChart3, CheckCircle2, AlertCircle } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { useApp } from "../../context/AppContext";
import { Panel, Overline } from "../common/Panel";

/* ThingSpeak channels:
   Ch-A (3287764): field1=V_L1 field2=V_L2 field3=V_L3
                   field4=I_L1 field5=I_L2 field6=I_L3 field7=W_L1 field8=W_L2
   Ch-B (3287770): field1=W_L3  field2=PF_L1 field3=PF_L2 field4=PF_L3
                   field5=Hz_L1 field6=Hz_L2 field7=Hz_L3 field8=kWh_Total
*/
const TS_BASE  = "https://api.thingspeak.com/channels/";
const TS_CH_A  = 3287764;
const TS_CH_B  = 3287770;
const TS_KEY_A = "UOE9JB0Z827Q9G2H";
const TS_KEY_B = "OLJUZUDTKL1SDK0G";

const RANGES = [
  { val: "1h",  label: "Last 1h",     minutes: 60 },
  { val: "24h", label: "Last 24h",    minutes: 1440 },
  { val: "7d",  label: "Last 7 days", minutes: 10080 },
];

const COLORS = { L1: "#ef4444", L2: "#f59e0b", L3: "#3b82f6", Total: "#10b981" };

function ChartCard({ title, unit, datasets, testid }) {
  return (
    <div className="bg-[var(--surface2)] border border-[var(--bd)] rounded-md p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-mono font-medium">{title}</span>
        <span className="text-[10px] font-mono text-[var(--t2)]">{unit}</span>
      </div>
      <ResponsiveContainer width="100%" height={150}>
        <LineChart margin={{ top: 4, right: 6, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--bd)" />
          <XAxis
            dataKey="time"
            type="category"
            allowDuplicatedCategory={false}
            tick={{ fontSize: 9, fontFamily: "JetBrains Mono", fill: "var(--t2)" }}
            stroke="var(--bd)"
          />
          <YAxis
            tick={{ fontSize: 9, fontFamily: "JetBrains Mono", fill: "var(--t2)" }}
            stroke="var(--bd)"
            domain={["auto", "auto"]}
          />
          <Tooltip
            contentStyle={{ background: "var(--surface)", border: "1px solid var(--bd-strong)", borderRadius: 8, fontFamily: "JetBrains Mono", fontSize: 11 }}
          />
          <Legend wrapperStyle={{ fontSize: 10, fontFamily: "JetBrains Mono" }} />
          {datasets.map((d) => (
            <Line
              key={d.key}
              type="monotone"
              data={d.data}
              dataKey="v"
              name={d.label}
              stroke={d.color}
              strokeWidth={1.5}
              dot={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function LinkCard({ title, desc, children }) {
  return (
    <Panel className="p-5">
      <Overline>{title}</Overline>
      <p className="text-sm text-[var(--t2)] mt-1 mb-3">{desc}</p>
      <div className="flex flex-wrap gap-2">{children}</div>
    </Panel>
  );
}

function LinkBtn({ children, testid, onClick, href }) {
  const cls = "inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-mono font-medium bg-[var(--surface2)] border border-[var(--bd)] hover:border-[var(--bd-strong)] transition-colors";
  if (href) return <a href={href} target="_blank" rel="noreferrer" data-testid={testid} className={cls}>{children}<ExternalLink size={12} /></a>;
  return <button onClick={onClick} data-testid={testid} className={cls}>{children}</button>;
}

export default function Grafana() {
  const { settings } = useApp();
  const [range, setRange] = useState("1h");
  const [charts, setCharts] = useState(null);
  const [status, setStatus] = useState("idle");
  const [statusMsg, setStatusMsg] = useState("");
  const timerRef = useRef(null);
  const rangeRef = useRef(range);

  const loadData = async (r) => {
    const cur = r ?? rangeRef.current;
    const cfg = RANGES.find((x) => x.val === cur) ?? RANGES[0];
    const results = Math.min(Math.ceil(cfg.minutes * 4), 800);
    setStatus("loading");
    setStatusMsg(`Fetching ThingSpeak data (last ${cfg.minutes < 60 ? cfg.minutes + " min" : cfg.minutes / 60 + "h"})…`);
    try {
      const [resA, resB] = await Promise.all([
        fetch(`${TS_BASE}${TS_CH_A}/feeds.json?api_key=${TS_KEY_A}&results=${results}`).then((x) => x.json()),
        fetch(`${TS_BASE}${TS_CH_B}/feeds.json?api_key=${TS_KEY_B}&results=${results}`).then((x) => x.json()),
      ]);

      const feedA = resA.feeds || [];
      const mapB = {};
      (resB.feeds || []).forEach((f) => { mapB[f.created_at] = f; });

      const vL1 = [], vL2 = [], vL3 = [];
      const iL1 = [], iL2 = [], iL3 = [];
      const wL1 = [], wL2 = [], wL3 = [], wTot = [];
      const pf1 = [], pf2 = [], pf3 = [];
      const hz1 = [], hz2 = [], hz3 = [];
      const kwh = [];

      feedA.forEach((fA) => {
        const fB = mapB[fA.created_at] || {};
        const t = new Date(fA.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        const p = (v) => parseFloat(v) || null;
        vL1.push({ time: t, v: p(fA.field1) }); vL2.push({ time: t, v: p(fA.field2) }); vL3.push({ time: t, v: p(fA.field3) });
        iL1.push({ time: t, v: p(fA.field4) }); iL2.push({ time: t, v: p(fA.field5) }); iL3.push({ time: t, v: p(fA.field6) });
        const wl1 = p(fA.field7) ?? 0, wl2 = p(fA.field8) ?? 0, wl3 = p(fB.field1) ?? 0;
        wL1.push({ time: t, v: wl1 || null }); wL2.push({ time: t, v: wl2 || null }); wL3.push({ time: t, v: wl3 || null });
        wTot.push({ time: t, v: (wl1 + wl2 + wl3) || null });
        pf1.push({ time: t, v: p(fB.field2) }); pf2.push({ time: t, v: p(fB.field3) }); pf3.push({ time: t, v: p(fB.field4) });
        hz1.push({ time: t, v: p(fB.field5) }); hz2.push({ time: t, v: p(fB.field6) }); hz3.push({ time: t, v: p(fB.field7) });
        kwh.push({ time: t, v: p(fB.field8) });
      });

      const lastTime = vL1[vL1.length - 1]?.time ?? "--";
      setStatusMsg(`${feedA.length} points · last: ${lastTime} · auto-refresh every 15s`);
      setStatus("ok");
      setCharts({
        voltage:   [{ key: "L1", label: "L1", data: vL1, color: COLORS.L1 }, { key: "L2", label: "L2", data: vL2, color: COLORS.L2 }, { key: "L3", label: "L3", data: vL3, color: COLORS.L3 }],
        current:   [{ key: "L1", label: "L1", data: iL1, color: COLORS.L1 }, { key: "L2", label: "L2", data: iL2, color: COLORS.L2 }, { key: "L3", label: "L3", data: iL3, color: COLORS.L3 }],
        power:     [{ key: "T", label: "Total", data: wTot, color: COLORS.Total }, { key: "L1", label: "L1", data: wL1, color: COLORS.L1 }, { key: "L2", label: "L2", data: wL2, color: COLORS.L2 }, { key: "L3", label: "L3", data: wL3, color: COLORS.L3 }],
        pf:        [{ key: "L1", label: "L1", data: pf1, color: COLORS.L1 }, { key: "L2", label: "L2", data: pf2, color: COLORS.L2 }, { key: "L3", label: "L3", data: pf3, color: COLORS.L3 }],
        frequency: [{ key: "L1", label: "L1", data: hz1, color: COLORS.L1 }, { key: "L2", label: "L2", data: hz2, color: COLORS.L2 }, { key: "L3", label: "L3", data: hz3, color: COLORS.L3 }],
        kwh:       [{ key: "T", label: "kWh Total", data: kwh, color: COLORS.Total }],
      });
    } catch (err) {
      setStatus("error");
      setStatusMsg("ThingSpeak error: " + err.message);
    }
  };

  useEffect(() => {
    rangeRef.current = range;
    loadData(range);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => loadData(), 15000);
    return () => clearInterval(timerRef.current);
  }, [range]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6">
      <div>
        <Overline>Cloud Platforms</Overline>
        <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">Data Integrations</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <LinkCard title="Grafana Cloud" desc="Time-series dashboards.">
          <LinkBtn href={`${settings.grafanaUrl}?orgId=1&from=now-1h&to=now&refresh=15s`} testid="grafana-1h">Last 1h</LinkBtn>
          <LinkBtn href={`${settings.grafanaUrl}?orgId=1&from=now-24h&to=now&refresh=30s`} testid="grafana-24h">Last 24h</LinkBtn>
          <LinkBtn href={`${settings.grafanaUrl}?orgId=1&from=now-7d&to=now`} testid="grafana-7d">Last 7 days</LinkBtn>
        </LinkCard>

        <LinkCard title="ThingSpeak" desc="IoT dual-channel analytics.">
          <LinkBtn href={`https://thingspeak.com/channels/${TS_CH_A}`} testid="thingspeak-a">Ch-A: V / I / W</LinkBtn>
          <LinkBtn href={`https://thingspeak.com/channels/${TS_CH_B}`} testid="thingspeak-b">Ch-B: PF / Hz / kWh</LinkBtn>
          <LinkBtn href={`${TS_BASE}${TS_CH_A}/feeds.json?api_key=${TS_KEY_A}&results=10`} testid="thingspeak-verify-a">
            <CheckCircle2 size={12} /> Verify Ch-A
          </LinkBtn>
          <LinkBtn href={`${TS_BASE}${TS_CH_B}/feeds.json?api_key=${TS_KEY_B}&results=10`} testid="thingspeak-verify-b">
            <CheckCircle2 size={12} /> Verify Ch-B
          </LinkBtn>
        </LinkCard>

        <LinkCard title="Sheets & Tableau" desc="Exports & BI reports.">
          <LinkBtn href="https://docs.google.com/spreadsheets/d/1_4sR5lyNkXI6wMRKJy0Vf1TKfMDi8R1kcBUn5RGvLu4/edit" testid="google-sheets">Google Sheets</LinkBtn>
          <LinkBtn href="https://script.google.com/macros/s/AKfycbwK3acHGdImSd5K6OUrxkU435zHEmmmqoiC75IVYmzY-fAEGQKpFceUjoKwq8gPYISQqA/exec" testid="apps-script">Apps Script</LinkBtn>
          <LinkBtn href="https://public.tableau.com/views/CampusEnergyMonitor/Dashboard1?:language=en-US&:sid=&:redirect=auth&:display_count=n&:origin=viz_share_link" testid="tableau">Tableau</LinkBtn>
        </LinkCard>
      </div>

      <Panel className="p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div className="flex items-center gap-2">
            <BarChart3 size={16} className="text-[var(--accent)]" />
            <Overline>ThingSpeak Historical Trends</Overline>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => loadData(range)}
              disabled={status === "loading"}
              className="w-8 h-8 rounded-md border border-[var(--bd)] flex items-center justify-center hover:border-[var(--bd-strong)] transition-colors disabled:opacity-50"
            >
              <RefreshCw size={13} className={status === "loading" ? "animate-spin" : ""} />
            </button>
            {RANGES.map(({ val, label }) => (
              <button
                key={val}
                onClick={() => setRange(val)}
                data-testid={`history-range-${val}`}
                className={`px-3 py-1.5 rounded-md text-xs font-mono transition-colors ${range === val ? "bg-[var(--accent)] text-black" : "bg-[var(--surface2)] border border-[var(--bd)] text-[var(--t2)] hover:text-[var(--t1)]"}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 mb-4 text-xs font-mono">
          {status === "ok"    && <CheckCircle2 size={12} className="text-[var(--accent)]" />}
          {status === "error" && <AlertCircle size={12} className="text-[var(--danger)]" />}
          {status === "loading" && <RefreshCw size={12} className="animate-spin text-[var(--t2)]" />}
          <span className={status === "error" ? "text-[var(--danger)]" : "text-[var(--t2)]"}>{statusMsg}</span>
        </div>

        {charts ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ChartCard title="Voltage"      unit="V"   datasets={charts.voltage}   testid="chart-voltage" />
            <ChartCard title="Current"      unit="A"   datasets={charts.current}   testid="chart-current" />
            <ChartCard title="Power"        unit="W"   datasets={charts.power}     testid="chart-power" />
            <ChartCard title="Power Factor" unit=""    datasets={charts.pf}        testid="chart-pf" />
            <ChartCard title="Frequency"    unit="Hz"  datasets={charts.frequency} testid="chart-frequency" />
            <ChartCard title="Energy"       unit="kWh" datasets={charts.kwh}       testid="chart-kwh" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {["voltage", "current", "power", "pf", "frequency", "kwh"].map((k) => (
              <div key={k} className="bg-[var(--surface2)] border border-[var(--bd)] rounded-md p-4 h-[198px] flex items-center justify-center" data-testid={`chart-${k}`}>
                <span className="font-mono text-xs text-[var(--t2)] animate-pulse">Loading ThingSpeak…</span>
              </div>
            ))}
          </div>
        )}

        <p className="text-[11px] text-[var(--t2)] font-mono mt-4">
          Data from ThingSpeak Ch-A ({TS_CH_A}) &amp; Ch-B ({TS_CH_B}) · updates every 15 s from ESP32 · auto-refresh every 15 s
        </p>
      </Panel>
    </div>
  );
}
